"use client";

import type { ReactNode } from "react";
import {
  COUNTRIES,
  GENDERS,
  draftStatus,
  expiryMessage,
  formatDay,
  licenseLast4,
  type DriverCardStatus,
  type DriverDraft,
  type DriverFormKey,
} from "./draft";
import type { RenterDriverCardsState } from "./useRenterDriverCards";

export interface RenterDriverCardsProps {
  state: RenterDriverCardsState;
  /** The return day (`YYYY-MM-DD`): every license must be valid through it. */
  returnDate: string;
  /** Why drivers are required: the insurer check wins over the fleet setting. */
  reason?: "fleet_setting" | "insurer_check";
  /** Locked, like the screening questions, once the hold has a client secret. */
  disabled?: boolean;
  className?: string;
}

export const INPUT_CLASS =
  "w-full rounded-lg border border-gray-300 px-3 py-2.5 text-base text-gray-900 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary disabled:opacity-50 sm:text-sm";
export const INPUT_ERROR_CLASS = INPUT_CLASS.replace(
  "border-gray-300",
  "border-destructive",
);
export const LINK_BUTTON_CLASS =
  "text-sm font-medium text-primary underline-offset-2 hover:underline disabled:opacity-50 disabled:no-underline";

export const STATUS_LABEL: Record<DriverCardStatus, string> = {
  needed: "License needed",
  in_progress: "In progress",
  ready: "Ready",
};

export const STATUS_CLASS: Record<DriverCardStatus, string> = {
  needed: "bg-amber-50 text-amber-800",
  in_progress: "bg-blue-50 text-blue-800",
  ready: "bg-green-50 text-green-800",
};

export function initials(name: string): string {
  const letters = name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase());

  return letters.join("") || "?";
}

interface FieldProps {
  id: string;
  label: string;
  optional?: boolean;
  error?: string;
  span?: boolean;
  children: ReactNode;
}

export function Field({
  id,
  label,
  optional,
  error,
  span,
  children,
}: FieldProps) {
  return (
    <div className={`flex min-w-0 flex-col ${span ? "sm:col-span-2" : ""}`}>
      <label
        htmlFor={id}
        className="mb-1.5 text-sm font-semibold text-gray-700"
      >
        {label}
        {optional && (
          <span className="ml-1 font-normal text-gray-500">optional</span>
        )}
      </label>
      {children}
      {error && (
        <p id={`${id}-error`} className="mt-1 text-sm text-red-600">
          {error}
        </p>
      )}
    </div>
  );
}

interface DriverCardProps {
  index: number;
  driver: DriverDraft;
  state: RenterDriverCardsState;
  returnDate: string;
  disabled: boolean;
}

function DriverCard({
  index,
  driver,
  state,
  returnDate,
  disabled,
}: DriverCardProps) {
  const contact = state.contactKeys(index);
  const status = draftStatus(driver, returnDate, contact);
  const errors = state.errors[driver.key] ?? {};
  const isMain = index === 0;
  const { fields } = driver;
  const name = [fields.given_names, fields.last_name]
    .map((part) => part.trim())
    .filter(Boolean)
    .join(" ");
  const title = name || (isMain ? "Main driver" : "Additional driver");
  const role = isMain
    ? state.bookerDrives
      ? "Main driver · you"
      : "Main driver"
    : "Additional driver";
  const needsSave = state.tried && status !== "ready";

  const fieldError = (key: DriverFormKey) =>
    errors[key] ??
    (key === "license_expires_on"
      ? expiryMessage(fields.license_expires_on, returnDate) || undefined
      : undefined);

  const input = (
    key: DriverFormKey,
    props: { type?: string; autoComplete?: string } = {},
  ) => {
    const id = `${driver.key}-${key}`;
    const error = fieldError(key);

    return (
      <input
        id={id}
        type={props.type ?? "text"}
        value={fields[key]}
        disabled={disabled}
        autoComplete={props.autoComplete ?? "off"}
        aria-invalid={!!error}
        aria-describedby={error ? `${id}-error` : undefined}
        className={error ? INPUT_ERROR_CLASS : INPUT_CLASS}
        onChange={(event) =>
          state.setField(driver.key, key, event.target.value)
        }
      />
    );
  };

  const asksEmail = contact.includes("email");
  const showPhone = contact.includes("phone");
  const emailLabel = isMain
    ? state.bookerDrives
      ? asksEmail
        ? "Email"
        : null
      : "Their email"
    : "Email";

  return (
    <div
      data-testid={`driver-card-${index}`}
      className={`rounded-xl border bg-white ${
        needsSave
          ? "border-destructive"
          : status === "ready"
            ? "border-green-200"
            : "border-blue-600 shadow-[0_0_0_3px_rgba(59,130,246,0.1)]"
      }`}
    >
      <div className="flex flex-wrap items-center gap-3 px-4 py-3.5">
        <span
          aria-hidden="true"
          className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${
            status === "ready"
              ? "bg-green-50 text-green-700"
              : "bg-gray-100 text-gray-700"
          }`}
        >
          {status === "ready" ? "✓" : initials(title)}
        </span>
        <span className="flex min-w-0 flex-1 flex-col">
          <b className="truncate text-sm text-gray-900">{title}</b>
          <small className="text-xs text-gray-500">{role}</small>
        </span>
        <span
          className={`rounded-full px-2.5 py-1 text-xs font-semibold ${STATUS_CLASS[status]}`}
        >
          {STATUS_LABEL[status]}
        </span>
        {!isMain && (
          <button
            type="button"
            className={LINK_BUTTON_CLASS}
            disabled={disabled}
            onClick={() => state.remove(driver.key)}
          >
            Remove
          </button>
        )}
      </div>

      <div className="border-t border-gray-100 px-4 py-4">
        {status === "ready" ? (
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2 text-sm text-gray-700">
            <span>
              License ending {licenseLast4(fields.license_number)} · expires{" "}
              {formatDay(fields.license_expires_on)}
            </span>
            <span className="rounded-full bg-green-50 px-2.5 py-1 text-xs font-semibold text-green-800">
              Valid through the return
            </span>
            <span className="flex-1" />
            <button
              type="button"
              className={LINK_BUTTON_CLASS}
              disabled={disabled}
              onClick={() => state.edit(driver.key)}
            >
              Edit
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {(emailLabel || showPhone) && (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                {emailLabel && (
                  <Field
                    id={`${driver.key}-email`}
                    label={emailLabel}
                    optional={!isMain && !asksEmail}
                    error={fieldError("email")}
                    span={!showPhone}
                  >
                    {input("email", { type: "email", autoComplete: "off" })}
                  </Field>
                )}
                {showPhone && (
                  <Field
                    id={`${driver.key}-phone`}
                    label="Phone"
                    error={fieldError("phone")}
                    span={!emailLabel}
                  >
                    {input("phone", { type: "tel", autoComplete: "off" })}
                  </Field>
                )}
              </div>
            )}

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field
                id={`${driver.key}-given_names`}
                label="First name(s), as on the license"
                error={fieldError("given_names")}
              >
                {input("given_names")}
              </Field>
              <Field
                id={`${driver.key}-last_name`}
                label="Last name, as on the license"
                error={fieldError("last_name")}
              >
                {input("last_name")}
              </Field>
              <Field
                id={`${driver.key}-date_of_birth`}
                label="Date of birth"
                error={fieldError("date_of_birth")}
              >
                {input("date_of_birth", { type: "date" })}
              </Field>
              <Field
                id={`${driver.key}-gender`}
                label="Gender"
                optional
                error={fieldError("gender")}
              >
                <select
                  id={`${driver.key}-gender`}
                  value={fields.gender}
                  disabled={disabled}
                  className={INPUT_CLASS}
                  onChange={(event) =>
                    state.setField(driver.key, "gender", event.target.value)
                  }
                >
                  <option value=""></option>
                  {GENDERS.map((gender) => (
                    <option key={gender} value={gender}>
                      {gender}
                    </option>
                  ))}
                </select>
              </Field>
              <Field
                id={`${driver.key}-license_number`}
                label="Driver's license number"
                error={fieldError("license_number")}
              >
                {input("license_number")}
              </Field>
              <Field
                id={`${driver.key}-license_expires_on`}
                label="Expiration date"
                error={fieldError("license_expires_on")}
              >
                {input("license_expires_on", { type: "date" })}
              </Field>
            </div>

            <div>
              <p className="mb-3 text-sm font-semibold text-gray-900">
                License address
              </p>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Field
                  id={`${driver.key}-license_street`}
                  label="Street address"
                  error={fieldError("license_street")}
                  span
                >
                  {input("license_street")}
                </Field>
                <Field
                  id={`${driver.key}-license_street_2`}
                  label="Apt, suite, unit"
                  optional
                  error={fieldError("license_street_2")}
                >
                  {input("license_street_2")}
                </Field>
                <Field
                  id={`${driver.key}-license_city`}
                  label="City"
                  error={fieldError("license_city")}
                >
                  {input("license_city")}
                </Field>
                <div className="grid grid-cols-1 gap-4 sm:col-span-2 sm:grid-cols-3">
                  <Field
                    id={`${driver.key}-license_state`}
                    label="State"
                    error={fieldError("license_state")}
                  >
                    {input("license_state")}
                  </Field>
                  <Field
                    id={`${driver.key}-license_zipcode`}
                    label="ZIP code"
                    error={fieldError("license_zipcode")}
                  >
                    {input("license_zipcode")}
                  </Field>
                  <Field
                    id={`${driver.key}-license_country`}
                    label="Country"
                    error={fieldError("license_country")}
                  >
                    <select
                      id={`${driver.key}-license_country`}
                      value={fields.license_country}
                      disabled={disabled}
                      className={INPUT_CLASS}
                      onChange={(event) =>
                        state.setField(
                          driver.key,
                          "license_country",
                          event.target.value,
                        )
                      }
                    >
                      {COUNTRIES.map((country) => (
                        <option key={country.value} value={country.value}>
                          {country.label}
                        </option>
                      ))}
                    </select>
                  </Field>
                </div>
              </div>
            </div>

            <button
              type="button"
              disabled={disabled}
              onClick={() => state.save(driver.key)}
              className="w-full rounded-md bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-50 sm:w-auto"
            >
              Save driver
            </button>
          </div>
        )}
      </div>

      {needsSave && (
        <p
          role="alert"
          className="border-t border-destructive/20 px-4 py-2.5 text-sm text-red-600"
        >
          Add and save this driver&apos;s license to continue.
        </p>
      )}
    </div>
  );
}

/**
 * The drivers section of the renter checkout: one card per driver, from the
 * FLEE-442 prototype. Presentational; `useRenterDriverCards` owns the state.
 * Shared later with the quote page.
 */
export function RenterDriverCards({
  state,
  returnDate,
  reason = "fleet_setting",
  disabled = false,
  className = "",
}: RenterDriverCardsProps) {
  const total = state.drivers.length;

  return (
    <div className={`space-y-4 ${className}`} data-testid="renter-driver-cards">
      <div className="flex flex-wrap items-baseline gap-3">
        <h2 className="text-xl font-semibold text-gray-900">Drivers</h2>
        <span className="text-sm text-gray-500">
          {state.readyCount} of {total} ready
        </span>
      </div>

      <div aria-hidden="true" className="flex gap-1">
        {state.drivers.map((driver, index) => (
          <i
            key={driver.key}
            className={`h-1.5 flex-1 rounded-full ${
              draftStatus(driver, returnDate, state.contactKeys(index)) ===
              "ready"
                ? "bg-green-600"
                : "bg-gray-200"
            }`}
          />
        ))}
      </div>

      <p className="text-sm text-gray-600">
        {reason === "insurer_check"
          ? "Trip insurance needs everyone who will drive the RV. Each driver gets a link by email and text to verify their identity."
          : "Everyone who will drive the RV."}{" "}
        Each license must be valid through the return on {formatDay(returnDate)}
        .
      </p>

      <label className="flex items-center gap-2 text-sm font-semibold text-gray-800">
        <input
          type="checkbox"
          checked={state.bookerDrives}
          disabled={disabled}
          onChange={(event) => state.setBookerDrives(event.target.checked)}
        />
        I&apos;m the main driver
      </label>

      {state.drivers.map((driver, index) => (
        <DriverCard
          key={driver.key}
          index={index}
          driver={driver}
          state={state}
          returnDate={returnDate}
          disabled={disabled}
        />
      ))}

      {state.canAdd && (
        <button
          type="button"
          disabled={disabled}
          onClick={state.add}
          className="flex w-full items-center justify-center gap-2 rounded-lg border border-dashed border-gray-300 px-4 py-3 text-sm font-medium text-gray-800 hover:border-primary hover:bg-blue-50 disabled:opacity-50"
        >
          + Add another driver
        </button>
      )}

      <p className="text-xs text-gray-500">License numbers are encrypted.</p>
    </div>
  );
}
